import React, { useState, useEffect, useCallback } from 'react';
import { crmDataService } from '../HR/CRM/utils/crmDataService';
import TicketForm from '../HR/CRM/components/TicketForm';
import TicketSidebar from '../HR/CRM/components/todotickets/TicketSidebar';
import ApprovalActionModal from '../HR/CRM/components/ApprovalActionModal';
import ApprovalTimeline from '../HR/CRM/components/ApprovalTimeline';
import '../HR/CRM/styles/crmTickets.css';

function TACRMTickets() {
  const [teams, setTeams] = useState([]);
  const [stores, setStores] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTicketFieldValues, setSelectedTicketFieldValues] = useState(null);
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [approvalModal, setApprovalModal] = useState({ open: false, ticket: null, action: null });
  const [approvalJustification, setApprovalJustification] = useState('');
  const [processingApproval, setProcessingApproval] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Check if we're on mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load initial data
  useEffect(() => {
    loadInitialData();
    loadCurrentUser();
  }, []);

  const loadCurrentUser = () => {
    // Get current user from localStorage
    const userEmail = localStorage.getItem('userEmail');
    const userRole = localStorage.getItem('userRole');

    setCurrentUser({
      email: userEmail,
      role: userRole,
      isAdmin: userRole?.toLowerCase() === 'admin' || userRole?.toLowerCase() === 'director' || userRole?.toLowerCase() === 'vp'
    });
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [teamsData, storesData, ticketsData] = await Promise.all([
        crmDataService.getTeams(),
        crmDataService.getStores(),
        crmDataService.searchTickets({ limit: 50 })
      ]);

      setTeams(teamsData);
      setStores(storesData);
      setTickets(ticketsData);
      setError(null);
    } catch (err) {
      setError('Failed to load initial data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPendingApprovals = useCallback(async () => {
    try {
      setLoadingApprovals(true);
      const approvals = await crmDataService.getPendingApprovals();
      setPendingApprovals(approvals);
    } catch (err) {
      console.error('Failed to load approvals:', err.message);
    } finally {
      setLoadingApprovals(false);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.email) {
      loadPendingApprovals();
    }
  }, [currentUser?.email, loadPendingApprovals]);

  const handleTicketCreated = async (ticket) => {
    setSuccessMessage(`Ticket ${ticket.ticket_number} created successfully!`);
    setTickets([ticket, ...tickets]);
    setSelectedTicket(null);
    setSelectedTicketFieldValues(null);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleApprovalActionClick = (approvalItem, actionType) => {
    setApprovalModal({
      open: true,
      ticket: approvalItem,
      action: actionType,
    });
    setApprovalJustification('');
  };

  const closeApprovalModal = () => {
    setApprovalModal({ open: false, ticket: null, action: null });
    setApprovalJustification('');
  };

  const submitApprovalAction = async () => {
    if (!approvalModal.ticket) {
      return;
    }

    try {
      setProcessingApproval(true);
      const result = await crmDataService.actOnApproval(
        approvalModal.ticket.ticket_number,
        approvalModal.action,
        approvalJustification
      );

      setSuccessMessage(
        `Ticket ${approvalModal.ticket.ticket_number} ${approvalModal.action === 'approve' ? 'approved' : 'rejected'
        } successfully!`
      );
      setTimeout(() => setSuccessMessage(''), 5000);

      closeApprovalModal();
      await loadPendingApprovals();

      setTickets((prev) =>
        prev.map((t) =>
          t.ticket_number === approvalModal.ticket.ticket_number
            ? { ...t, status: result.ticket?.status || t.status }
            : t
        )
      );

      if (
        selectedTicket?.ticket_number === approvalModal.ticket.ticket_number &&
        result.ticket
      ) {
        setSelectedTicket({
          ...selectedTicket,
          ...result.ticket,
          approval_flow: result.approval_flow || selectedTicket.approval_flow,
        });
      }
    } catch (err) {
      setError('Failed to process approval: ' + err.message);
    } finally {
      setProcessingApproval(false);
    }
  };

  const handleTicketClick = async (ticket) => {
    try {
      setLoadingTicket(true);
      setError(null);

      // Fetch full ticket details
      const ticketDetails = await crmDataService.getTicketByNumber(ticket.ticket_number);

      // Fetch conditional field values if ticket has an ID
      let fieldValues = {};
      if (ticketDetails.id) {
        try {
          fieldValues = await crmDataService.getTicketFieldValues(ticketDetails.id);
        } catch (err) {
          console.warn('No conditional field values found for ticket:', err);
        }
      }

      setSelectedTicket(ticketDetails);
      setSelectedTicketFieldValues(fieldValues);

      // Close mobile sidebar after selecting a ticket
      if (isMobile) {
        setIsMobileSidebarOpen(false);
      }
    } catch (err) {
      setError('Failed to load ticket details: ' + err.message);
    } finally {
      setLoadingTicket(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedTicket(null);
    setSelectedTicketFieldValues(null);
  };

  const handleRefreshTickets = async () => {
    try {
      const ticketsData = await crmDataService.searchTickets({ limit: 50 });
      setTickets(ticketsData);
    } catch (err) {
      setError('Failed to refresh tickets: ' + err.message);
    }
  };

  // Filter tickets based on user role - only show TA team tickets
  const getFilteredTickets = () => {
    // First filter to only TA team tickets
    const taTickets = tickets.filter(ticket => ticket.team_code === 'TA');

    if (!currentUser) return taTickets;

    // Admins see all TA tickets
    if (currentUser.isAdmin) {
      return taTickets;
    }

    // Regular users see only their own TA tickets
    return taTickets.filter(ticket =>
      ticket.requester_email === currentUser.email ||
      ticket.created_by === currentUser.email ||
      ticket.assigned_to === currentUser.email
    );
  };

  if (loading) {
    return (
      <div className="crm-tickets-loading">
        <div className="loading-spinner"></div>
        <p>Loading TA Requests...</p>
      </div>
    );
  }

  const filteredTickets = getFilteredTickets();
  const selectedTicketRequiresApproval =
    selectedTicket?.team_id === 1 && selectedTicket?.category_id === 25;

  return (
    <div className="crm-tickets-container">
      {/* Alert Messages */}
      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">!</span>
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>x</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span className="alert-icon">OK</span>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Approvals Panel - Now shown inside tickets instead */}
      {/* {showApprovalsPanel && (
        <ApprovalsPanel
          approvals={pendingApprovals}
          loading={loadingApprovals}
          error={approvalError}
          onRefresh={loadPendingApprovals}
          onAction={handleApprovalActionClick}
        />
      )} */}

      {/* Mobile Toggle Button */}
      {isMobile && (
        <button
          className="mobile-sidebar-toggle"
          onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        >
          <span className="toggle-icon">{isMobileSidebarOpen ? '✕' : '☰'}</span>
          <span className="toggle-text">{isMobileSidebarOpen ? 'Close' : 'My Tickets'}</span>
          {!isMobileSidebarOpen && filteredTickets.length > 0 && (
            <span className="toggle-badge">{filteredTickets.length}</span>
          )}
        </button>
      )}

      {/* Two Column Layout: Sidebar + Main Content */}
      <div className={`crm-tickets-layout ${isMobile ? 'mobile-layout' : ''}`}>
        {/* Mobile Overlay */}
        {isMobile && isMobileSidebarOpen && (
          <div
            className="mobile-sidebar-overlay"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}

        {/* Left Sidebar - Tickets List */}
        <div className={`sidebar-wrapper ${isMobile ? (isMobileSidebarOpen ? 'mobile-open' : 'mobile-closed') : ''}`}>
          <TicketSidebar
            tickets={filteredTickets}
            selectedTicket={selectedTicket}
            onTicketSelect={handleTicketClick}
            onRefresh={handleRefreshTickets}
            stores={stores}
            pendingApprovals={pendingApprovals}
          />
        </div>

        {/* Right Main Content - Ticket Form */}
        <main className="tickets-main-content">
          <div className="main-content-header">
            <h2>{selectedTicket ? `Ticket #${selectedTicket.ticket_number}` : 'Create New Ticket'}</h2>
            {selectedTicket && (
              <button className="btn-secondary small" onClick={handleClearSelection}>
                Back to Create New Ticket
              </button>
            )}
          </div>
          {selectedTicketRequiresApproval &&
            selectedTicket?.approval_flow &&
            selectedTicket.approval_flow.length > 0 && (
              <div className="approval-timeline-wrapper">
                <ApprovalTimeline
                  flow={selectedTicket.approval_flow}
                  ticketStatus={selectedTicket.status}
                />
              </div>
            )}
          <div className="ticket-form-wrapper">
            {loadingTicket ? (
              <div className="form-loading">
                <div className="loading-spinner"></div>
                <p>Loading ticket details...</p>
              </div>
            ) : (
              <TicketForm
                teams={teams}
                stores={stores}
                onTicketCreated={handleTicketCreated}
                viewMode={!!selectedTicket}
                ticketData={selectedTicket}
                ticketFieldValues={selectedTicketFieldValues}
                pendingApproval={pendingApprovals.find(
                  (a) => a.ticket_number === selectedTicket?.ticket_number
                )}
                onApprovalAction={handleApprovalActionClick}
                defaultTeamCode="TA"
                lockTeam={true}
                defaultCategoryName="Talent Acquisition"
                lockCategory={true}
              />
            )}
          </div>
        </main>
      </div>

      <ApprovalActionModal
        open={approvalModal.open}
        ticket={approvalModal.ticket}
        action={approvalModal.action}
        justification={approvalJustification}
        onChange={setApprovalJustification}
        onClose={closeApprovalModal}
        onSubmit={submitApprovalAction}
        loading={processingApproval}
      />
    </div>
  );
}

export default TACRMTickets;
